<template>
  <div class="md-layout">
    <!-- Inspection Details: Title, Invoice Number, and Date -->
    <div v-if="inspectionDetail" class="inspection-detail-container">
      <ul class="inspection-detail-list">
        <li><strong>Title:</strong> {{ inspectionDetail.title }}</li>
        <li><strong>Invoice Number:</strong> {{ inspectionDetail.invoiceNumber }}</li>
        <li><strong>Date:</strong> {{ inspectionDetail.date }}</li>
        <li><strong>Instruction File:</strong> 
          <a :href="inspectionDetail.fileUrl" target="_blank">{{ inspectionDetail.instructionFile }}</a>
        </li>
      </ul>
    </div>

    <!-- Download Button aligned to the right -->
    <div class="download-btn-container" v-if="inspections.length > 0">
      <button class="btn btn-primary" @click="downloadImages()" :disabled="isDisabled">
        Download All Images <span v-if="isDisabled"><i class="fa fa-spinner fa-spin"></i></span>
      </button>
    </div>

    <!-- Iterate over the inspection data -->
    <div v-for="(inspection, index) in inspections" :key="index" class="md-layout-item md-size-100">
      <!-- Category Card -->
      <div class="category-card">
        <!-- Category Name (e.g., Sampling, Marking) -->
        <div class="category-title">
          <h3>{{ inspection.category }}</h3>
        </div>

        <!-- Image Grid for the Category -->
        <div class="md-layout md-layout-wrap">
          <div v-for="(image, idx) in inspection.images" :key="idx" class="md-layout-item md-size-25">
            <!-- Image Card with Thumbnails -->
            <div class="image-card">
              <img :src="image"  @click="openPreview(image)" style="cursor: pointer" alt="Inspection Image" class="thumbnail" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showPreview" class="image-modal" @click.self="closePreview">
      <span class="close-btn" @click="closePreview">&times;</span>
      <img :src="selectedImage" class="preview-image" />
    </div>

  </div>
</template>

<script>
import InspectionsService from "../../services/InspectionsService";
import JSZip from "jszip";
import { saveAs } from "file-saver";

export default {
  data() {
    return {
      inspectionId: this.$route.query.id,
      isDisabled: false,
      inspections: [],
      inspectionDetail: null,
      showPreview: false,      // Controls modal visibility
      selectedImage: null      // Stores the clicked image URL
    };
  },
  methods: {
    async getInspectionData() {
      this.isDisabled = true;
      let data = { inspectionId: this.inspectionId };
      await InspectionsService.getInspectionData(data)
        .then((response) => {
          this.isDisabled = false;
          this.inspections = response.data.data;
          this.inspectionDetail = response.data.inspectionDetail;
        }).catch((e) => {
          this.isDisabled = false;
          this.$toast.error(e.response.data.respones_msg);
        });
    },
    openPreview(imageUrl) {
      this.selectedImage = imageUrl;
      this.showPreview = true;
    },
    closePreview() {
      this.showPreview = false;
      this.selectedImage = null;
      this.removeEscapeListener();
    },
    handleEscape(event) {
      if (event.key === 'Escape') {
        this.closePreview();
      }
    },
    addEscapeListener() {
      document.addEventListener('keydown', this.handleEscape);
    },
    removeEscapeListener() {
      document.removeEventListener('keydown', this.handleEscape);
    },
    
    async downloadImages() {
        this.isDisabled = true;
      const zip = new JSZip();

      // Loop over each inspection data
      for (let inspection of this.inspections) {
        const categoryFolder = zip.folder(inspection.category); // Create folder for each category

        for (let imageUrl of inspection.images) {
          const imageName = imageUrl.split('/').pop(); // Extract the image name from the URL

          // Fetch image as a Blob
          const response = await fetch(imageUrl);
          const blob = await response.blob();

          // Add image to the category folder
          categoryFolder.file(imageName, blob);
        }
      }

      // Generate the zip file and trigger download
      zip.generateAsync({ type: "blob" }).then((content) => {
        saveAs(content, this.inspectionDetail.title+".zip");
        this.isDisabled = false;
      });
    }
  },
  watch: {
    showPreview(newVal) {
      if (newVal) {
        this.addEscapeListener();
      } else {
        this.removeEscapeListener();
      }
    },
  },
  beforeMount() {
    this.getInspectionData();
    this.removeEscapeListener();
  }
};
</script>

<style lang="scss" scoped>

  /* General styling for layout */
  
  /* Inspection Details */
  .inspection-detail-container {
    margin-bottom: 20px;
  }
  
  .inspection-detail-list {
    list-style: none;
    padding-left: 0;
    font-size: 1.2rem;
    margin: 0;
  }

  .inspection-detail-list li {
    margin-bottom: 8px;
    padding: 8px;
    background-color: #f7f7f7;
    border-radius: 5px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    font-weight: normal;
  }

  .inspection-detail-list li strong {
    font-weight: bold;
    color: #333;
  }

  .category-card {
    background-color: #f9f9f9;
    border-radius: 8px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    margin-bottom: 20px;
    padding: 20px;
  }

  .category-title {
    margin-bottom: 20px;
    text-align: center;
    font-size: 1.5rem;
    font-weight: bold;
  }

  /* Grid for category images */
  .md-layout-wrap {
    display: flex;
    flex-wrap: wrap;
    gap: 15px;
  }

  .md-layout-item {
    padding: 10px;
  }

  /* Image card style for thumbnail */
  .image-card {
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    transition: transform 0.3s ease;
    text-align: center;
  }

  /* Thumbnail image style */
  .thumbnail {
    width: 100%;
    max-width: 120px; /* Set max size for thumbnails */
    height: auto;
    border-radius: 8px;
    margin: 0 auto;
    transition: transform 0.3s ease;
  }

  /* Hover effect on image card */
  .image-card:hover {
    transform: scale(1.05);
  }

  /* Styling for the download button */
  .download-btn-container {
    display: flex;
    justify-content: flex-end; /* Align the button to the right */
    width: 100%; /* Ensure it occupies full width */
  }

  .btn {
    background-color: #007bff;
    border: none;
    color: white;
    padding: 10px 20px;
    font-size: 16px;
    border-radius: 5px;
    cursor: pointer;
    transition: background-color 0.3s ease;
  }

  .btn:hover {
    background-color: #0056b3;
  }

.image-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0,0,0,0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.preview-image {
  max-width: 90%;
  max-height: 90%;
  border-radius: 8px;
}

.close-btn {
  position: absolute;
  top: 30px;
  right: 40px;
  font-size: 3rem;
  color: #fff;
  cursor: pointer;
}

</style>
